import { Component, OnInit, OnDestroy, input } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  UntypedFormArray,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { IArticles } from 'src/app/@interfaces/article.interface';
import { IWarehouse } from 'src/app/@interfaces/warehouse.interface';
import { InventoryService } from 'src/app/@pages/inventory/inventory.service';
import { WarehouseService } from 'src/app/@pages/warehouse/warehouse.service';
import { ValidatorsService } from 'src/app/@shared/validator/validators.service';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';
import { ITaxes } from 'src/app/@interfaces/taxes.interface';
import { ITypes } from 'src/app/@interfaces/types.interface';
import { IUnits } from 'src/app/@interfaces/units.interface';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { IGrupoArt } from 'src/app/@interfaces/grupoart.interface';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { DialogComponent } from '../../dialog.component';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';
import { NgxSpinnerModule } from 'ngx-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommonModule } from '@angular/common';
import { Observable, of, map, startWith, switchMap } from 'rxjs';
import { CompoundArticlesService } from 'src/app/@pages/inventory/components/compound-articles/services/compound-articles.service';
import { AlertsService } from '../../../services/alerts/alerts.service';
import {
  CompoundArticleDetail,
  CompoundArticleUpdateInput,
  CompoundArticleUpdateResponse,
} from '@shared-models';
import { MatSelectModule } from '@angular/material/select';
import {
  IAlternativeCode,
  IAlternativeCodeInput,
} from 'src/app/@interfaces/alternative-code.interface';
import { AlternativeCodeDialogComponent } from '../../alternative-code-dialog/alternative-code-dialog.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProviderService } from 'src/app/@pages/provider/provider.service';
import { UnitConversionService } from 'src/app/@shared/services/unit-conversion.service';

@Component({
  selector: 'app-modify-article',
  templateUrl: './modify-article.component.html',
  styleUrls: ['./modify-article.component.css'],
  imports: [
    CommonModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    NgxSpinnerModule,
    TranslatePipe,
    MatSelectModule,
    MatTooltipModule,
  ],
})
export class ModifyArticleComponent implements OnInit, OnDestroy {
  id_article = input<number>(0);
  isCompoundArticle = input<boolean>(false);
  id_compound_article = input<string | undefined>(undefined);

  warehouseList: Array<IWarehouse> = [];

  // Compound Article specific properties
  compoundArticle?: CompoundArticleDetail;
  filteredArticles: Observable<IArticles[]>[] = [];
  availableUnitsPerComponent: IUnits[][] = []; // Units with conversions for each component
  taxesList: Array<ITaxes> = [];
  typesList: Array<ITypes> = [];
  unitsList: Array<IUnits> = [];
  grupoartList: Array<IGrupoArt> = [];
  articleModify = {} as IArticles;

  articleForm: UntypedFormGroup = this.fb.group({
    code: ['', Validators.required],
    description: ['', Validators.required],
    presentation: [
      '',
      [
        Validators.required,
        Validators.pattern(this.validatorS.quantityPattern),
      ],
    ],
    price: [
      '',
      [
        Validators.required,
        Validators.pattern(this.validatorS.quantityPattern),
      ],
    ],
    quantity: [
      '',
      [
        Validators.required,
        Validators.pattern(this.validatorS.quantityPattern),
      ],
    ],
    group: ['', Validators.required],
    presentation_id: ['', Validators.required],
    unit: ['', Validators.required],
    warehouse: [''],
    tax: ['', Validators.required],
    type: ['', Validators.required],
    unitBilly: ['', Validators.required],
    packagingUnit: [null],
    packagingFactor1: [
      1,
      [Validators.pattern(this.validatorS.quantityPattern)],
    ],
    packagingFactor2: [
      1,
      [Validators.pattern(this.validatorS.quantityPattern)],
    ],
    quantityInitial: [
      '',
      [Validators.pattern(this.validatorS.quantityPattern)],
    ],
    valueInitial: ['', [Validators.pattern(this.validatorS.quantityPattern)]],
  });

  unsubscribe$ = new Subject<void>();
  companyId = '';
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  alternativeCodes: string[] = [];
  alternativeCodesList: IAlternativeCodeInput[] = [];
  providersList: any[] = [];
  isPackagingFactorLocked = false; // Controls if packaging factor is locked due to standard conversion

  constructor(
    private router: Router,
    private fb: UntypedFormBuilder,
    private validatorS: ValidatorsService,
    private warehouseS: WarehouseService,
    private inventoryS: InventoryService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private cookieAuthService: CookieAuthService,
    private compoundArticlesService: CompoundArticlesService,
    private alertsService: AlertsService,
    private providerService: ProviderService,
    private unitConversionService: UnitConversionService,
  ) {}

  // Getter for FormArray
  get componentsFormArray(): UntypedFormArray {
    return this.articleForm.get('components') as UntypedFormArray;
  }

  ngOnInit(): void {
    this.getAuthValues();

    // Load the article data (simple or compound)
    if (this.isCompoundArticle() && this.id_compound_article()) {
      // Load compound article data
      this.loadCompoundArticle();
    } else {
      // Primero cargar TODAS las listas necesarias
      this.warehouseS
        .listWarehouses()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((result) => {
          this.warehouseList = result;
        });

      this.inventoryS
        .listTaxes()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((result) => {
          this.taxesList = result;
        });

      this.inventoryS
        .listTypes()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((result) => {
          this.typesList = result;
        });

      this.inventoryS
        .listGrupoArt()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((result) => {
          this.grupoartList = result;
        });

      // Cargar unitsList PRIMERO, luego cargar el artículo
      this.inventoryS
        .listUnits()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((result) => {
          this.unitsList = result;

          // DESPUÉS de cargar las unidades, cargar el artículo
          this.inventoryS
            .getArticle(this.id_article())
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((articleResult) => {
              this.articleModify = articleResult;
              this.setArticle(this.articleModify);
              this.alternativeCodes =
                articleResult.alternativeCodes?.map(
                  (code: IAlternativeCode) => code.alternative_code,
                ) || [];

              // Load alternative codes from new table
              if (
                articleResult.alternativeCodes &&
                articleResult.alternativeCodes.length > 0
              ) {
                this.alternativeCodesList = articleResult.alternativeCodes.map(
                  (code: any) => ({
                    alternative_code: code.alternative_code,
                    id_provider: code.id_provider
                      ? Number(code.id_provider)
                      : undefined,
                    id_unit: code.id_unit ? Number(code.id_unit) : undefined,
                    packaging_quantity: code.packaging_quantity,
                    units_per_package: code.units_per_package,
                    conversion_factor: code.conversion_factor,
                    description: code.description,
                    provider: code.provider, // Preserve nested provider object
                    unit: code.unit, // Preserve nested unit object
                  }),
                );
              }

              // Load providers for alternative codes
              this.loadProviders();

              // Check if packaging factor should be locked after loading article
              this.onPackagingUnitChange();
            });
        });
    }

    // Listen to packaging unit changes
    this.articleForm
      .get('packagingUnit')
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.onPackagingUnitChange();
      });
  }

  getAuthValues() {
    const company = this.cookieAuthService.getCompanyObject;
    this.companyId = company!.Id_company!.toString();
  }

  setArticle(articleSet: IArticles) {
    // Use actual saved values instead of factorizing
    let factor1 = articleSet.packaging_quantity || 1;
    let factor2 = articleSet.units_per_package || 1;

    let selectedPackagingUnit: IUnits | null = null;
    if (articleSet.packagingUnit && articleSet.packagingUnit.id_unit) {
      selectedPackagingUnit =
        this.unitsList.find(
          (u) => u.id_unit === articleSet.packagingUnit!.id_unit,
        ) || articleSet.packagingUnit;
    }

    this.articleForm.patchValue({
      code: articleSet.codigo,
      description: articleSet.descripcion,
      presentation: articleSet.presentacion,
      price: articleSet.costo,
      quantity: articleSet.cantidad,
      group: articleSet.grupoArt![0],
      presentation_id: articleSet.id_type2,
      unit: articleSet.unit?.id_second || '',
      warehouse: articleSet.almacen![0],
      tax: articleSet.tax![0],
      type: articleSet.type![0],
      unitBilly: articleSet.unit,
      packagingUnit: selectedPackagingUnit,
      packagingFactor1: factor1,
      packagingFactor2: factor2,
    });
  }

  // Getter to calculate total factor
  get packagingFactorTotal(): number {
    // Use getRawValue to access disabled fields (when factor is locked)
    const rawValues = this.articleForm.getRawValue();
    const factor1 = Number(rawValues.packagingFactor1) || 1;
    const factor2 = Number(rawValues.packagingFactor2) || 1;
    return factor1 * factor2;
  }

  // Comparator for packaging unit
  comparePackagingUnits(u1: IUnits | null, u2: IUnits | null): boolean {
    // If both are null or undefined, they are equal
    if (!u1 && !u2) return true;
    // If only one is null/undefined, they are not equal
    if (!u1 || !u2) return false;
    // Compare by id_unit
    return u1.id_unit === u2.id_unit;
  }

  update() {
    this.articleForm.markAllAsTouched();
    if (this.articleForm.invalid === true) {
      this.alertsService.showErrorAlert(
        this.translate.instant('There are required fields. Please fill them'),
      );
      return;
    }

    if (this.isCompoundArticle()) {
      this.updateCompoundArticle();
    } else {
      this.updateSimpleArticle();
    }
  }

  private updateSimpleArticle(): void {
    // Use getRawValue() to get values from disabled fields (when factor is locked)
    const formValues = this.articleForm.getRawValue();

    let presentation;
    if (formValues.group.presentations) {
      presentation = parseInt(formValues.group.presentations[0].id_type);
    } else {
      presentation = parseInt(formValues.presentation_id);
    }

    const packagingUnit = formValues.packagingUnit;
    const packagingUnitId = packagingUnit
      ? parseInt(packagingUnit.id_unit)
      : null;
    const packagingFactor = this.packagingFactorTotal;
    const packagingQuantity = packagingUnit
      ? Number(formValues.packagingFactor1 || 1)
      : undefined;
    const unitsPerPackage = packagingUnit
      ? Number(formValues.packagingFactor2 || 1)
      : undefined;

    this.inventoryS
      .updateArticle(
        this.id_article(),
        formValues.code,
        formValues.description,
        String(formValues.presentation || ''),
        formValues.price,
        formValues.quantity,
        parseInt(formValues.group.id_grupo),
        formValues.unit,
        parseInt(formValues.tax.id_tax),
        parseInt(formValues.type.id_type),
        parseInt(formValues.unitBilly.id_unit),
        presentation,
        packagingUnitId,
        packagingFactor,
        packagingQuantity,
        unitsPerPackage,
        this.cleanAlternativeCodesForBackend(), // Clean nested objects before sending
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (result) {
          this.alertsService
            .showSuccessfullAlert(
              this.translate.instant('Article has been updated successfully'),
            )
            .then((result) => {
              if (result.isConfirmed) {
                this.router
                  .navigateByUrl('/table', { skipLocationChange: true })
                  .then(() => {
                    this.router.navigate(['/inventory']);
                  });
              }
            });
        } else {
          this.alertsService.showErrorAlert(
            this.translate.instant('Something was wrong'),
          );
        }
      });
  }

  private updateCompoundArticle(): void {
    if (!this.id_compound_article()) {
      this.alertsService.showErrorAlert(
        'inventory.compoundArticles.modals.modify.invalidIdError',
      );
      return;
    }

    // Prepare compound article update data
    const compoundArticleToUpdate = this.prepareCompoundArticleUpdateData();

    this.compoundArticlesService
      .updateCompoundArticle(
        compoundArticleToUpdate as unknown as CompoundArticleUpdateInput,
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (updatedCompoundArticle: CompoundArticleUpdateResponse) => {
          this.alertsService
            .showSuccessfullAlert(
              this.translate.instant(
                'inventory.compoundArticles.modals.modify.success',
              ),
            )
            .then((result) => {
              if (result.isConfirmed) {
                this.router
                  .navigateByUrl('/table', { skipLocationChange: true })
                  .then(() => {
                    this.router.navigate(['/inventory'], {
                      queryParams: { tab: 'compound' },
                    });
                  });

                this.closeDialog();
              }
            });
        },
        error: (error) => {
          this.alertsService.showErrorAlert(
            'inventory.compoundArticles.modals.modify.error',
          );
        },
      });
  }

  private prepareCompoundArticleUpdateData(): any {
    const formValue = this.articleForm.value;

    let presentation;
    if (formValue.group.presentations) {
      presentation = parseInt(formValue.group.presentations[0].id_type);
    } else {
      presentation = parseInt(formValue.presentation_id);
    }

    // Prepare components data with unit information
    const components = formValue.components.map((component: any) => ({
      id_component_article: parseInt(component.article.id_articulo),
      quantity: parseFloat(component.quantity),
      id_unit: parseInt(component.unit), // Include the unit ID from the formula
    }));

    return {
      id_compound_article: this.id_compound_article(),
      codigo: formValue.code,
      descripcion: formValue.description,
      presentacion: formValue.presentation,
      costo: parseFloat(formValue.price),
      grupo: parseInt(formValue.group.id_grupo),
      unidad: formValue.unit,
      tax: parseInt(formValue.tax.id_tax),
      tipo: parseInt(formValue.type.id_type),
      unidad2: parseInt(formValue.unitBilly.id_unit),
      id_type2: presentation,
      alternativeCodes: this.cleanAlternativeCodesForBackend(), // Clean nested objects before sending
      compoundArticle: {
        quantity: parseFloat(formValue.recipeQuantity),
        is_active: true,
        components: components,
      },
    };
  }

  warehousesDiaglog(article: IArticles) {
    this.dialog.open(DialogComponent, {
      data: { warehouseOpenings: true, article: article },
    });
  }

  closeDialog() {
    this.dialog.closeAll();
  }

  textValid(text: string) {
    return (
      this.articleForm.get(text)?.invalid && this.articleForm.get(text)?.touched
    );
  }

  compareGroups(item1: any, item2: any) {
    if (item1 === null || item2 === null) {
      return false;
    }
    return item1.id_grupo === item2.id_grupo;
  }

  compareUnits(item1: any, item2: any) {
    if (item1 === null || item2 === null) {
      return false;
    }
    return item1.id_unit === item2.id_unit;
  }

  compareType(item1: any, item2: any) {
    if (item1 === null || item2 === null) {
      return false;
    }
    return item1.id_type === item2.id_type;
  }

  compareTax(item1: any, item2: any) {
    if (item1 === null || item2 === null) {
      return false;
    }
    return item1.id_tax === item2.id_tax;
  }

  compareWarehouse(item1: any, item2: any) {
    if (item1 === null || item2 === null) {
      return false;
    }
    return item1.id_almacen === item2.id_almacen;
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.alternativeCodes.push(value);
    }
  }

  remove(alternativeCode: string): void {
    const index = this.alternativeCodes.indexOf(alternativeCode);
    if (index >= 0) {
      this.alternativeCodes.splice(index, 1);
    }
  }

  // Alternative Codes Methods
  loadProviders(): void {
    this.providerService
      .listProvider(null, null)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (result) => {
          // The API returns { count, providers }
          this.providersList = result.providers || [];
        },
        error: (error) => {
          console.error('Error loading providers:', error);
          this.providersList = [];
        },
      });
  }

  async onPackagingUnitChange(): Promise<void> {
    // Get base unit - in modify-article, 'unit' field stores id_second (text)
    const baseUnitCode = this.articleForm.get('unit')?.value; // e.g., "KG", "G"
    const baseUnit = this.unitsList.find((u) => u.id_second === baseUnitCode);
    const baseUnitId = baseUnit?.id_unit;

    // Get packaging unit ID
    const packagingUnit = this.articleForm.get('packagingUnit')?.value;
    const packagingUnitId = packagingUnit?.id_unit;

    // Reset factor locked state
    this.isPackagingFactorLocked = false;

    // If no packaging unit selected, reset factors and unlock
    if (!packagingUnitId) {
      this.articleForm.patchValue({
        packagingFactor1: 1,
        packagingFactor2: 1,
      });
      this.articleForm.get('packagingFactor1')?.enable();
      this.articleForm.get('packagingFactor2')?.enable();
      return;
    }

    // If no base unit selected, allow manual factor
    if (!baseUnitId) {
      this.articleForm.get('packagingFactor1')?.enable();
      this.articleForm.get('packagingFactor2')?.enable();
      return;
    }

    // Check if there's a standard conversion
    try {
      // For packaging, we need to know: how many base units are in one packaging unit?
      // So we ask for conversion FROM packaging TO base (e.g., Kilogram -> Gram = 1000)
      const hasConversion = await this.unitConversionService.hasConversion(
        Number(packagingUnitId), // FROM packaging unit
        Number(baseUnitId), // TO base unit
      );

      if (hasConversion) {
        // Get the conversion factor from database
        // This gives us: how many base units in one packaging unit
        const factor = await this.unitConversionService
          .getConversionFactor(Number(packagingUnitId), Number(baseUnitId))
          .toPromise();

        if (factor && factor > 0) {
          // Lock the factor and apply standard conversion
          this.isPackagingFactorLocked = true;
          this.articleForm.patchValue({
            packagingFactor1: factor,
            packagingFactor2: 1,
          });

          // Disable the factor fields
          this.articleForm.get('packagingFactor1')?.disable();
          this.articleForm.get('packagingFactor2')?.disable();
        }
      } else {
        // No standard conversion, allow custom factor
        this.isPackagingFactorLocked = false;

        // Enable the factor fields for manual input
        this.articleForm.get('packagingFactor1')?.enable();
        this.articleForm.get('packagingFactor2')?.enable();
      }
    } catch (error) {
      console.error('Error checking unit conversion:', error);
      // On error, allow custom factor
      this.isPackagingFactorLocked = false;
      this.articleForm.get('packagingFactor1')?.enable();
      this.articleForm.get('packagingFactor2')?.enable();
    }
  }

  openAlternativeCodeDialog(
    code?: IAlternativeCodeInput,
    index?: number,
  ): void {
    const dialogRef = this.dialog.open(AlternativeCodeDialogComponent, {
      width: '600px',
      data: {
        providers: this.providersList,
        units: this.unitsList,
        alternativeCode: code,
        articlePackagingUnit: this.articleForm.value.packagingUnit,
        articleBaseUnit: this.articleForm.value.unitBilly, // Pass base unit for conversion validation
      },
    });

    dialogRef.afterClosed().subscribe((result: IAlternativeCodeInput) => {
      if (result) {
        if (index !== undefined) {
          // Edit existing code
          this.alternativeCodesList[index] = result;
        } else {
          // Add new code
          this.alternativeCodesList.push(result);
        }
      }
    });
  }

  removeAlternativeCode(index: number): void {
    this.alternativeCodesList.splice(index, 1);
  }

  getProviderName(code: IAlternativeCodeInput): string {
    // First try to use nested provider object from backend
    if (code.provider?.nombre) {
      return code.provider.nombre;
    }
    // Fallback to lookup in providersList
    if (!code.id_provider) return '-';
    const provider = this.providersList.find(
      (p) => String(p.id_provider) === String(code.id_provider),
    );
    return provider ? provider.nombre : '-';
  }

  getUnitName(code: IAlternativeCodeInput): string {
    // First try to use nested unit object from backend
    if (code.unit?.description) {
      return `${code.unit.description} (${code.unit.dian_code})`;
    }
    // Fallback to lookup in unitsList
    if (!code.id_unit) return 'Same as article';
    const unit = this.unitsList.find(
      (u) => String(u.id_unit) === String(code.id_unit),
    );
    return unit ? `${unit.description} (${unit.dian_code})` : '-';
  }

  // Compound Article Methods
  private loadCompoundArticle(): void {
    if (!this.id_compound_article()) return;

    // PRIMERO cargar unitsList, luego el compoundArticle
    this.inventoryS
      .listUnits()
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap((units) => {
          this.unitsList = units;
          return this.compoundArticlesService.getCompoundArticle(
            this.id_compound_article()!,
          );
        }),
      )
      .subscribe({
        next: (compoundArticle: CompoundArticleDetail) => {
          this.compoundArticle = compoundArticle;
          this.articleModify = compoundArticle.baseArticle;

          // Set the article basic data
          this.setArticle(this.articleModify);
          this.alternativeCodes =
            this.articleModify.alternativeCodes?.map(
              (code: IAlternativeCode) => code.alternative_code,
            ) || [];

          // Load alternative codes from new table
          if (
            this.articleModify.alternativeCodes &&
            this.articleModify.alternativeCodes.length > 0
          ) {
            this.alternativeCodesList = this.articleModify.alternativeCodes.map(
              (code: any) => ({
                alternative_code: code.alternative_code,
                id_provider: code.id_provider
                  ? Number(code.id_provider)
                  : undefined,
                id_unit: code.id_unit ? Number(code.id_unit) : undefined,
                packaging_quantity: code.packaging_quantity,
                units_per_package: code.units_per_package,
                conversion_factor: code.conversion_factor,
                description: code.description,
                provider: code.provider,
                unit: code.unit,
              }),
            );
          }

          // Load providers for alternative codes
          this.loadProviders();

          // Initialize compound article specific form
          this.initializeCompoundArticleForm();
        },
        error: (error) => {
          this.alertsService.showErrorAlert(
            'inventory.compoundArticles.modals.modify.errorLoadingData',
          );
        },
      });
  }

  private initializeCompoundArticleForm(): void {
    if (!this.compoundArticle) return;

    // Add compound article specific controls
    this.articleForm.addControl(
      'recipeQuantity',
      this.fb.control(this.compoundArticle.quantity, [
        Validators.required,
        Validators.pattern(this.validatorS.quantityPattern),
        Validators.min(0.001),
      ]),
    );

    this.articleForm.addControl('components', this.fb.array([]));

    // Load existing sub-articles
    this.loadExistingSubArticles();

    // Update form validators for compound articles
    this.updateFormValidators();
  }

  private loadExistingSubArticles(): void {
    if (!this.compoundArticle?.subArticles) return;

    // Clear existing components
    while (this.componentsFormArray.length !== 0) {
      this.componentsFormArray.removeAt(0);
    }

    // Add existing sub-articles to the FormArray
    this.compoundArticle.subArticles.forEach((subArticle, index) => {
      // Use formula unit (id_unit from compound_article_components)
      // Priority: subArticle.unit.id_unit (formula unit object) > subArticle.id_unit > baseArticle defaults
      const unitValue =
        subArticle.unit?.id_unit ||
        subArticle.id_unit ||
        subArticle.baseArticle.id_unit ||
        subArticle.baseArticle.unit?.id_unit ||
        0;

      // Try to get the complete unit object from unitsList first (has all fields like id_second)
      // This ensures consistent display format with the registration form
      let selectedUnit: IUnits | undefined;

      // First try: Find in unitsList to get complete unit with all fields (id_second, etc.)
      if (unitValue) {
        selectedUnit = this.unitsList.find((u) => u.id_unit === unitValue);
      }

      // Second try: Use the unit object from the formula if not found in unitsList
      if (!selectedUnit && subArticle.unit && subArticle.unit.id_unit) {
        selectedUnit = {
          id_unit: subArticle.unit.id_unit,
          description: subArticle.unit.description || '',
          dian_code: subArticle.unit.dian_code || '',
          id_second: subArticle.unit.id_second || '',
        };
      }

      // Initialize availableUnitsPerComponent with at least the selected unit
      // This ensures Angular Material mat-select can render the selected value immediately
      if (selectedUnit) {
        this.availableUnitsPerComponent[index] = [selectedUnit];
      } else {
        // Fallback to full unitsList if no unit found (instead of empty array)
        this.availableUnitsPerComponent[index] = [...this.unitsList];
      }

      // Create FormGroup with initial values from the start
      const componentGroup = this.fb.group({
        article: [subArticle.baseArticle, Validators.required],
        articleSearch: [
          `${subArticle.baseArticle.descripcion} - ${subArticle.baseArticle.codigo}`,
        ],
        quantity: [
          subArticle.quantity,
          [
            Validators.required,
            Validators.pattern(this.validatorS.quantityPattern),
            Validators.min(0.001),
          ],
        ],
        unit: [unitValue, Validators.required],
      });

      // Add to FormArray - autocomplete initializes automatically via getter
      this.componentsFormArray.push(componentGroup);

      // Load available units with conversions for this component (only if valid unit)
      // This will update the array asynchronously with all available units
      if (unitValue && unitValue !== 0) {
        this.loadAvailableUnitsForComponent(index, unitValue);
      }
    });
  }

  private loadAvailableUnitsForComponent(
    componentIndex: number,
    baseUnitId: number,
  ): void {
    // Preserve the currently selected unit before making API call
    const currentUnits = this.availableUnitsPerComponent[componentIndex] || [];
    const currentSelectedUnit = currentUnits[0]; // First unit is the initially selected one

    if (!baseUnitId || baseUnitId === 0) {
      console.warn(
        `[loadAvailableUnitsForComponent] Invalid baseUnitId for component ${componentIndex}`,
      );
      // Preserve existing selection or fallback to unitsList
      if (currentUnits.length === 0) {
        this.availableUnitsPerComponent[componentIndex] = [...this.unitsList];
      }
      return;
    }

    this.unitConversionService
      .getAvailableConversionsFromUnit(baseUnitId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (conversions) => {
          // Map conversions to IUnits format
          const availableUnits: IUnits[] = conversions.map((conv) => ({
            id_unit: conv.unit.id_unit,
            description: conv.unit.description,
            dian_code: conv.unit.dian_code,
            id_second: conv.unit.id_second,
          }));

          // Always include the base unit at the beginning
          const baseUnit = this.unitsList.find((u) => u.id_unit === baseUnitId);
          if (baseUnit) {
            const baseUnitExists = availableUnits.some(
              (u) => u.id_unit === baseUnitId,
            );
            if (!baseUnitExists) {
              availableUnits.unshift(baseUnit);
            }
          }

          // Also preserve the initially selected unit if different from base
          if (
            currentSelectedUnit &&
            currentSelectedUnit.id_unit !== baseUnitId &&
            !availableUnits.some(
              (u) => u.id_unit === currentSelectedUnit.id_unit,
            )
          ) {
            availableUnits.unshift(currentSelectedUnit);
          }

          // If no units available after all attempts, fallback to unitsList
          this.availableUnitsPerComponent[componentIndex] =
            availableUnits.length > 0 ? availableUnits : [...this.unitsList];
        },
        error: (error) => {
          console.error(
            `[loadAvailableUnitsForComponent] Error loading conversions for component ${componentIndex}:`,
            error,
          );
          // On error, preserve existing units or fallback to unitsList
          if (currentUnits.length > 0) {
            // Keep existing - don't overwrite
            return;
          }
          // Fallback to unitsList if nothing was set
          const baseUnit = this.unitsList.find((u) => u.id_unit === baseUnitId);
          this.availableUnitsPerComponent[componentIndex] = baseUnit
            ? [baseUnit]
            : [...this.unitsList];
        },
      });
  }

  private updateFormValidators(): void {
    if (!this.isCompoundArticle()) return;

    // Remove simple article fields for compound articles
    const simpleArticleFields = ['quantity'];

    simpleArticleFields.forEach((fieldName) => {
      if (this.articleForm.get(fieldName)) {
        this.articleForm.removeControl(fieldName);
      }
    });

    // Set up components validation
    this.componentsFormArray.setValidators(this.minLengthArray(1));
  }

  // Custom validator for FormArray minimum length
  private minLengthArray(min: number) {
    return (control: any) => {
      const array = control as UntypedFormArray;
      return array.length < min
        ? { minLengthArray: { min, actual: array.length } }
        : null;
    };
  }

  // FormArray management methods
  createComponentFormGroup(initialData?: any): UntypedFormGroup {
    return this.fb.group({
      article: [initialData?.article || '', Validators.required],
      articleSearch: [initialData?.articleSearch || ''],
      quantity: [
        initialData?.quantity || '',
        [
          Validators.required,
          Validators.pattern(this.validatorS.quantityPattern),
          Validators.min(0.001),
        ],
      ],
      unit: [initialData?.unit || '', Validators.required],
    });
  }

  // Lazy getter to obtain autocomplete observable
  // Auto-initializes the first time the template requests it
  getFilteredArticles(index: number): Observable<IArticles[]> {
    if (!this.filteredArticles[index]) {
      const componentGroup = this.componentsFormArray.at(index);
      const articleSearchControl = componentGroup?.get('articleSearch');

      if (articleSearchControl) {
        this.filteredArticles[index] = articleSearchControl.valueChanges.pipe(
          startWith(articleSearchControl.value || ''),
          switchMap((value) =>
            this.performArticleSearch(
              typeof value === 'string' ? value : value?.descripcion || '',
            ),
          ),
        );
      } else {
        this.filteredArticles[index] = of([]);
      }
    }
    return this.filteredArticles[index];
  }

  private performArticleSearch(searchTerm: string): Observable<IArticles[]> {
    if (!searchTerm || searchTerm.length < 2) {
      return of([]);
    }

    return this.inventoryS
      .searchSimpleArticles(searchTerm, null, null, null)
      .pipe(
        map((result) => result.articles || []),
        takeUntil(this.unsubscribe$),
      );
  }

  // Get available units with conversions for a specific component
  getAvailableUnitsForComponent(index: number): IUnits[] {
    const units = this.availableUnitsPerComponent[index];
    // Fallback to unitsList if undefined, null, or empty array
    return units && units.length > 0 ? units : this.unitsList;
  }

  // Sub-article management methods
  addSubArticle(): void {
    if (!this.isCompoundArticle()) return;

    const componentGroup = this.createComponentFormGroup();
    this.componentsFormArray.push(componentGroup);

    // Initialize empty units array for new component
    const newIndex = this.componentsFormArray.length - 1;
    this.availableUnitsPerComponent[newIndex] = this.unitsList;

    // El autocomplete se inicializa automáticamente vía getter getFilteredArticles
  }

  removeSubArticle(index: number): void {
    if (!this.isCompoundArticle()) return;

    if (this.componentsFormArray.length > 1) {
      this.componentsFormArray.removeAt(index);
      this.filteredArticles.splice(index, 1);
      this.availableUnitsPerComponent.splice(index, 1);
    } else {
      this.alertsService.showWarningAlert(
        this.translate.instant(
          'inventory.compoundArticles.modals.register.subArticles.removeWarning',
        ),
      );
    }
  }

  onArticleSelected(selectedArticle: IArticles, index: number): void {
    const componentGroup = this.componentsFormArray.at(index);
    componentGroup.get('article')?.setValue(selectedArticle);
    componentGroup
      .get('articleSearch')
      ?.setValue(`${selectedArticle.descripcion} - ${selectedArticle.codigo}`);

    // Load available units with conversions for the selected article
    const baseUnitId =
      selectedArticle.unit?.id_unit || selectedArticle.id_unit || 0;
    if (baseUnitId) {
      // Set the default unit from the article
      componentGroup.get('unit')?.setValue(baseUnitId);
      // Load available conversions for this unit
      this.loadAvailableUnitsForComponent(index, baseUnitId);
    }
  }

  // Clean alternative codes for backend (remove nested objects)
  private cleanAlternativeCodesForBackend(): IAlternativeCodeInput[] {
    return this.alternativeCodesList.map((code) => ({
      alternative_code: code.alternative_code,
      id_provider: code.id_provider,
      id_unit: code.id_unit,
      packaging_quantity: code.packaging_quantity,
      units_per_package: code.units_per_package,
      conversion_factor: code.conversion_factor,
      description: code.description,
      // Exclude nested objects (provider, unit) - these are for display only
    }));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
